/**
 * Theme Store
 * Manages dark/light theme preference with localStorage persistence
 */

type Theme = 'light' | 'dark';

class ThemeStore {
  theme = $state<Theme>('light');

  constructor() {
    // Initialize from localStorage or system preference
    if (typeof window !== 'undefined') {
      this.loadTheme();
    }
  }

  private loadTheme() {
    // Check localStorage first
    const stored = localStorage.getItem('legere:theme') as Theme | null;
    if (stored === 'light' || stored === 'dark') {
      this.theme = stored;
      this.applyTheme(stored);
      return;
    }

    // Fall back to system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.theme = prefersDark ? 'dark' : 'light';
    this.applyTheme(this.theme);

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem('legere:theme')) {
        this.theme = e.matches ? 'dark' : 'light';
        this.applyTheme(this.theme);
      }
    });
  }

  private applyTheme(theme: Theme) {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggle() {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('legere:theme', this.theme);
    this.applyTheme(this.theme);
  }

  setTheme(theme: Theme) {
    this.theme = theme;
    localStorage.setItem('legere:theme', theme);
    this.applyTheme(theme);
  }
}

export const themeStore = new ThemeStore();
